import { API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import ExoStepper, { FinalPage } from '../exo/ExoStepper';
import StepRecipient from '../steps/StepRecipient';
import StepDelivery from '../steps/StepDelivery';
import StepCustomStartUp from '../steps/StepCustomStartUp';
import findDifferences from '../exo/findDifferencesInDataCollection';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { StepTemplate } from '../steps/StepTemplate';
import { PositionImage } from '../exo/positions/collapse-modules/PositionImage';
import { PositionDescription } from '../exo/positions/collapse-modules/PositionDescription';
import { PositionNumber } from '../exo/positions/collapse-modules/PositionNumber';
import { PositionText } from '../exo/positions/collapse-modules/PositionText';
import { PositionCheckbox } from '../exo/positions/collapse-modules/PositionCheckbox';
import { PositionProgressPaymentCalculation } from '../exo/positions/collapse-modules/PositionProgressPaymentCalcilation';
import { PositionPreCalculation } from '../exo/positions/collapse-modules/PositionPreCalculation';
import { PositionCopy } from '../exo/positions/collapse-modules/PositionCopy';
import StepPositionsOrder from '../steps/StepPositionsOrder';
import useFetch from '../../api/useFetch';
import SelectPositions from '../select/SelectPositions';
import StepOrder from '../steps/StepOrder';
import StepOrderInvoives from '../steps/StepOrderInvoices';
import StepUser from '../steps/StepUser';
import {
  hasInlineAttachments,
  StepInlineAttachments,
} from '../steps/StepInlineAttachments';
import StepTemplateMetaFields from '../steps/StepTemplateMetaFields';
import { Alert, Box, TextField, Typography, Tooltip } from '@mui/material';
import { numberToString, stringToNumber } from '../special/numberConverter';
import { round } from '../special/round';

// X-Invoice codes for identifing the type of invoice
export const finalInvoiceCode = [877];
export const progressBillCodes = [875, 876, 877, 326, 877];

const NewProgressBilling = ({
  invoices,
  setInvoices,
  onClose,
  startData,
  onUpdate,
  debug,
}) => {
  const { t } = useTranslation();
  const config = useConfig();
  const [steps, setSteps] = useState(generateSteps());

  useEffect(() => {
    if (!startData) return;

    let projId;

    if (
      startData.positions &&
      startData.positions.length > 0 &&
      startData.positions[0].project &&
      startData.positions[0].project.length > 0
    ) {
      projId = startData.positions[0].project[0].id;
      let onlyProject = true;

      startData.positions?.forEach((pos) => {
        if (pos.project?.length != 1 && pos.project?.id != projId) {
          onlyProject = false;
        }
      });

      if (onlyProject) {
        startData.project = startData.positions[0].project[0];
      }
    }
  }, [startData]);

  function getPaymentOptions() {
    let options = [];
    options = config ? config?.invoice.paymentOptions.options : [];
    let selection = config ? config?.invoice.paymentOptions.value : [];

    const filteredOptions = options.filter((option) =>
      selection.includes(option.value),
    );
    return filteredOptions;
  }

  useEffect(() => {
    setSteps(generateSteps());
  }, [startData, config]);

  function generateSteps() {
    var requiredFields = [
      {
        label: t('Title'),
        type: 'text',
        key: 'title',
      },
      {
        label: t("Recipient's Addition"),
        type: 'text',
        key: 'recipientAddition',
      },
      {
        label: t('Invoice Date'),
        type: 'date',
        key: 'date',
      },
      {
        label: t('Delivery Date'),
        type: 'date',
        key: 'deliveryDate',
      },
      {
        label: t('Payment Deadline in Days'),
        type: 'number',
        key: 'paymentDeadlineInDays',
        decimalPlaces: 0,
      },
      {
        label: t('Payment Method'),
        type: 'customSelect',
        key: 'paymentMethod',
        noUnselect: true,
        options: getPaymentOptions(),
      },
      {
        label: `${t('Introduction')} (${t('optional')})`,
        type: 'description',
        key: 'introduction',
        multiline: true,
        textBlockType: 'invoice',
      },
      {
        label: `${t('Document note')} (${t('optional')})`,
        type: 'description',
        key: 'note',
        multiline: true,
        textBlockType: 'invoice',
      },
      {
        label: `${t('Internal Note')} (${t('optional')})`,
        type: 'description',
        key: 'internalNote',
        multiline: true,
        textBlockType: 'invoice',
      },
    ];
    var requiredValidationSchema = Yup.object().shape({
      title: Yup.string().required(t('Title is required')),
      date: Yup.string().required(t('Date is required')),
      deliveryDate: Yup.string().required(t('Date is required')),
      paymentDeadlineInDays: Yup.number().required(
        t('Payment Deadline in Days is required'),
      ),
      paymentMethod: Yup.number().required(),
    });
    if (config) {
      const useDeliveryDateRange = startData
        ? Boolean(startData.deliveryDateStart)
        : Boolean(parseInt(config.invoice.useDeliveryDateRange));

      requiredFields = [
        {
          label: t('Title'),
          type: 'text',
          key: 'title',
        },
        {
          label: t("Recipient's Addition"),
          type: 'text',
          key: 'recipientAddition',
        },
        {
          label: `${t('Project')} (${t('optional')})`,
          type: 'project', //Change to "projectSearch" when TypeProjectSearch is done and we want to make the project searchable
          key: 'project',
          apiUrl: 'projects',
        },
        {
          label: t('Invoice Date'),
          type: 'date',
          key: 'date',
        },
        {
          label: useDeliveryDateRange
            ? t('Delivery Date Range')
            : t('Delivery Date'),
          type: useDeliveryDateRange ? 'dateRange' : 'date',
          key: useDeliveryDateRange ? 'deliveryDate' : 'deliveryDateEnd',
        },
        {
          label: t('Payment Deadline in Days'),
          type: 'number',
          key: 'paymentDeadlineInDays',
          decimalPlaces: 0,
        },
        {
          label: t('Payment Method'),
          type: 'customSelect',
          key: 'paymentMethod',
          noUnselect: true,
          options: getPaymentOptions(),
        },
        {
          label: `${t('Leitweg ID')} (${t('optional')})`,
          type: 'text',
          key: 'leitwegId',
        },
        {
          label: `${t('Introduction')} (${t('optional')})`,
          type: 'description',
          key: 'introduction',
          multiline: true,
          textBlockType: 'invoice',
        },
        {
          label: `${t('Document note')} (${t('optional')})`,
          type: 'description',
          key: 'note',
          multiline: true,
          textBlockType: 'invoice',
        },
        {
          label: `${t('Internal Note')} (${t('optional')})`,
          type: 'description',
          key: 'internalNote',
          multiline: true,
          textBlockType: 'invoice',
        },
      ];
      requiredValidationSchema = useDeliveryDateRange
        ? Yup.object().shape({
            title: Yup.string().required(t('Title is required')),
            date: Yup.string().required(t('Date is required')),
            deliveryDateStart: Yup.string().required(t('Date is required')),
            deliveryDateEnd: Yup.string().required(t('Date is required')),
            paymentDeadlineInDays: Yup.number().required(
              t('Payment Deadline in Days is required'),
            ),
            paymentMethod: Yup.number().required(),
            leitwegId: Yup.string()
              .nullable()
              .max(46, t('Leitweg ID must be at most 46 characters')),
          })
        : Yup.object().shape({
            title: Yup.string().required(t('Title is required')),
            date: Yup.string().required(t('Date is required')),
            deliveryDateEnd: Yup.string().required(t('Date is required')),
            paymentDeadlineInDays: Yup.number().required(
              t('Payment Deadline in Days is required'),
            ),
            paymentMethod: Yup.number().required(),
            leitwegId: Yup.string()
              .nullable()
              .max(46, t('Leitweg ID must be at most 46 characters')),
          });
    }

    const optionalFields = [
      {
        label: `${t('Discount')} (${t('optional')})`,
        type: 'checkbox',
        key: 'discount',
      },
      {
        label: `${t('Discount Rate')} (${t('optional')})`,
        type: 'number',
        key: 'discountRate',
        decimalPlaces: 2,
      },
      {
        label: `${t('Discount Days To Pay')} (${t('optional')})`,
        type: 'number',
        key: 'discountDaysToPay',
        decimalPlaces: 0,
      },
      {
        label: `${t('Images')} (${t('optional')})`,
        type: 'images',
        key: 'additionalFiles',
        maxItems: '15',
        maxSizeKB: '3000',
      },
    ];

    var collapsePositionModules = [
      { component: PositionImage, key: 'image', label: 'Image' },
      {
        component: PositionDescription,
        key: 'description',
        label: 'Description',
        textBlockType: 'invoice',
      },
      {
        component: PositionNumber,
        key: 'taxRate',
        label: 'Tax Rate',
        unit: '%',
        decimalPlaces: 0,
      },
      {
        component: PositionText,
        key: 'unit',
        label: 'Unit',
      },
      {
        component: PositionCopy,
        label: 'Duplicate position',
      },
    ];

    if (startData && startData.sourceType === 'order')
      collapsePositionModules = [
        ...collapsePositionModules,
        {
          component: PositionCheckbox,
          key: 'isOrderPosition',
          label: 'Settle from order',
        },
        {
          component: PositionProgressPaymentCalculation,
          key: 'price',
          label: 'Progress Payment Calculation',
        },
      ];

    collapsePositionModules = [
      ...collapsePositionModules,
      {
        component: PositionPreCalculation,
        key: 'price',
        label: 'Calculation of sales price',
      },
    ];

    return [
      {
        label: t('Order'),
        valid: true,
        content: ({
          updateDataCollection,
          activeStep,
          setActiveStep,
          step,
          dataCollection,
          updateValidation,
        }) => (
          <StepOrder
            onClose={onClose}
            dataCollection={dataCollection}
            updateDataCollection={updateDataCollection}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            step={step}
            updateValidation={updateValidation}
            noTemplate
            removeDrafts={true}
            includeProject={!(startData && startData.id)}
          />
        ),
      },
      {
        label: t('Recipient'),
        isHiddenFunction: (data) => Boolean(data?.order?.id),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepRecipient
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
          />
        ),
      },
      {
        label: t('Template'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
          setSteps,
          activeStep,
        }) => (
          <StepTemplate
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            templateType="partial_invoice"
            activeStep={activeStep}
            setSteps={setSteps}
          />
        ),
      },
      {
        label: t('Invoices'),
        valid: false,
        content: ({
          updateDataCollection,
          activeStep,
          setActiveStep,
          step,
          dataCollection,
          updateValidation,
        }) => (
          <StepOrderInvoives
            onClose={onClose}
            dataCollection={dataCollection}
            updateDataCollection={updateDataCollection}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            step={step}
            updateValidation={updateValidation}
          />
        ),
      },

      {
        label: t('Info'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepCustomStartUp
            fields={requiredFields}
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            validationSchema={requiredValidationSchema}
          />
        ),
      },
      {
        label: t('Additional'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepTemplateMetaFields
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            templateMetaData={dataCollection.templateMetaData}
          />
        ),
        isHiddenFunction: (data) =>
          !Boolean(data.templateMetaData?.metaFields?.length > 0),
      },
      {
        label: `(${t('Optional')})`,
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepCustomStartUp
            fields={optionalFields}
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
          />
        ),
      },
      {
        label: t('Order Positions'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepSelectOrderPositions
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
          />
        ),
        isHiddenFunction: (data) =>
          finalInvoiceCode.includes(data?.templateMetaData?.typeCode) ||
          !data?.order ||
          data?.isFlat,
      },
      {
        label: t('Custom Positions'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepPositionsOrder
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            hideTimeBilling={false}
            hideHOAI={false}
            collapseModules={[
              { component: PositionImage, key: 'image', label: 'Image' },
              {
                component: PositionDescription,
                key: 'description',
                label: 'Description',
                textBlockType: 'invoice',
              },
              {
                component: PositionNumber,
                key: 'taxRate',
                label: 'Tax Rate',
                unit: '%',
                decimalPlaces: 0,
              },
              {
                component: PositionText,
                key: 'unit',
                label: 'Unit',
              },
              {
                component: PositionCopy,
                label: 'Duplicate position',
              },
              {
                component: PositionCheckbox,
                key: 'isOrderPosition',
                label: 'Settle from order',
              },
              {
                component: PositionProgressPaymentCalculation,
                key: 'price',
                label: 'Progress Payment Calculation',
              },
            ]}
            imageType={`INVOICE_ATTACHMENT`}
            imageApiFilter={
              startData && startData.id
                ? `orderImages[has]=${startData.id}`
                : `orderImages[doesnothave]=0`
            }
          />
        ),
      },
      {
        label: t('Attachments'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepInlineAttachments
            dataCollection={dataCollection}
            updateDataCollection={updateDataCollection}
            updateValidation={updateValidation}
          />
        ),
        isHiddenFunction: (data) => !hasInlineAttachments(data?.positions),
      },
      {
        label: t('Contact Person'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepUser
            updateDataCollection={updateDataCollection}
            updateValidation={updateValidation}
            dataCollection={dataCollection}
          />
        ),
      },
      {
        label: t('Delivery'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepDelivery
            useInvoiceMail={true}
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            defaultRecipientMailKey="isInvoiceMail"
            allowMailContent={true}
            textBlockType={'invoice'}
          />
        ),
      },
    ];
  }

  const finalStep = {
    operation: ({
      dataCollection,
      handleResponse,
      setIsLoading,
      handleError,
    }) => {
      const diff = findDifferences(startData || {}, dataCollection);

      if (!diff.order) diff.sourceType = 'custom';

      DBRequest({
        config,
        path: dataCollection.id ? `invoices/${dataCollection.id}` : `invoices`,
        method: dataCollection.id
          ? API_PATCH({ ...diff, recipientType: dataCollection.recipientType })
          : API_POST(dataCollection),
        onResponse: handleRes,
        onLoading: setIsLoading,
        onError: handleError,
      });

      function handleRes(data) {
        handleResponse(data);
        onUpdate(data);
      }
    }, // your operation with the data
    content: (response) => (
      <FinalPage response={response} data={invoices} setData={setInvoices} />
    ), // component with final operations and the feedback
  };

  const [defaultData, setDefaultData] = useState({});

  useEffect(() => {
    if (!config) return;
    setSteps(generateSteps());
    const today = moment().format();

    const useDeliveryDateRange = Boolean(
      parseInt(config.invoice.useDeliveryDateRange),
    );
    const newDefaultData = {
      date: today,
      deliveryDateStart: useDeliveryDateRange ? today : null,
      deliveryDateEnd: today,
      paymentDeadlineInDays: config.invoice.paymentDeadlineInDays,
      discountRate: config.invoice.discountRate,
      orderTaxRate: config.invoice.discountRate,
      discountDaysToPay: config.invoice.discountPaymentDeadlineInDays,
      useDeliveryDateRange,
      sourceType: 'order',
      paymentMethod: getPaymentOptions()[0].value,
    };
    setDefaultData(newDefaultData);
  }, [config]);

  const correctedStartData = startData
    ? {
        ...startData,
        positions: completePositionsData(
          startData?.positions,
          startData?.orderPositions,
        ),
      }
    : {};

  return (
    <ExoStepper
      debug={debug}
      allowDraft
      confirmInvoice
      draftType={'progress-invoice'}
      label={
        startData && startData.id
          ? t('Edit Order Based Invoice')
          : t('New Order Based Invoice')
      }
      startDataCollection={
        startData && startData.id
          ? correctedStartData
          : { ...defaultData, ...correctedStartData }
      }
      message={t('Create Invoice')}
      onClose={onClose}
      data={steps}
      finalStep={finalStep}
      previewDocument="partial_invoice"
    />
  );
};

function hasMoreThanTwoDecimals(num) {
  if (!num) return false;
  return Math.abs(round(num, 2) - num) > 1e-9;
}

const TargetInput = ({ value, decimals = 2, onCommit }) => {
  const [displayValue, setDisplayValue] = useState(
    numberToString(value, decimals),
  );

  useEffect(() => {
    setDisplayValue(numberToString(value, decimals));
  }, [value, decimals]);

  return (
    <TextField
      size="small"
      value={displayValue}
      onChange={(e) => setDisplayValue(e.target.value)}
      onBlur={() => onCommit(stringToNumber(displayValue, decimals))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.target.blur();
        }
      }}
      inputProps={{
        style: {
          textAlign: 'right',
          width: 70,
          padding: '4px 8px',
        },
      }}
    />
  );
};

const StepSelectOrderPositions = ({
  dataCollection,
  updateValidation,
  updateDataCollection,
}) => {
  const { t } = useTranslation();
  //load positions
  const { apiData, isLoading } = useFetch(
    `orders/${dataCollection?.order?.id}`,
  );

  const orderPositionsIds = dataCollection.positions?.map((pos) => pos.id);
  const [convertedPositions, setConvertedPositions] = useState([]);
  const [targets, setTargets] = useState({});
  const [selectedRawPositions, setSelectedRawPositions] = useState([]);

  function processPositions(positions, currentTargets) {
    var newPositions = [];
    const converted = [];

    positions.forEach((element) => {
      const usePercent =
        element.type === 'hoaiPhase' || element.type === 'percent';
      const billedAmount = element.billedAmount || 0;

      // Use target if set, otherwise full open amount
      let openAmount;
      let openMonetaryFromTarget = null;
      const targetKey = element.id;
      let posType = element.type;
      let posPrice = element.price;

      const roundPrecision =
        posType == 'default'
          ? 2
          : posType == 'percent'
            ? 4
            : posType == 'hoaiPhase'
              ? 4
              : 2;
      if (
        currentTargets[targetKey] != null &&
        !element.isGroup &&
        element.type !== 'percent'
      ) {
        const targetRaw = currentTargets[targetKey];
        const target = usePercent ? targetRaw / 100 : targetRaw;
        // Compute monetary amount directly to avoid precision loss from quantity subtraction
        openMonetaryFromTarget = round(
          round(target * element.price, 2) - (element.billedTotal || 0),
          2,
        );
        openAmount = round(target - billedAmount, roundPrecision);
      } else {
        openAmount = billedAmount
          ? round(element.amount - billedAmount, roundPrecision)
          : element.amount;
      }

      let isConverted = false;

      const roundedOpenAmount = round(openAmount, roundPrecision);
      const exactTotal = round(openAmount * posPrice, 2);
      const roundedTotal = round(roundedOpenAmount * posPrice, 2);

      const openTotal =
        openMonetaryFromTarget != null
          ? openMonetaryFromTarget
          : round(element.amount * posPrice, 2) - (element.billedTotal || 0);

      if (exactTotal !== roundedTotal || roundedTotal !== openTotal) {
        let openMonetary;
        if (openMonetaryFromTarget != null) {
          openMonetary = openMonetaryFromTarget;
        } else {
          const totalOrdered = round(element.amount * element.price, 2);
          const totalBilled = element.billedTotal || 0;
          openMonetary = round(totalOrdered - totalBilled, 2);
        }
        openAmount = 1;
        posType = 'default';
        posPrice = openMonetary;
        converted.push(element.name || element.position);
      } else {
        openAmount = roundedOpenAmount;
        // For target-based: derive price from monetary amount to avoid precision drift
        if (openMonetaryFromTarget != null && openAmount !== 0) {
          posPrice = round(openMonetaryFromTarget / openAmount, 2);
        }
      }

      newPositions.push({
        ...element,
        isOptional: false,
        orderPositionId: element.id,
        isOrderPosition: true,
        totalAmount: element.amount,
        orderedAmount: element.amount,
        orderedPrice: element.price,
        billedAmount: element.billedAmount || 0,
        amount: round(openAmount, roundPrecision),
        type: posType,
        price: round(posPrice, 2),
      });
    });

    setConvertedPositions(converted);
    updateDataCollection({ positions: sortByPosition(newPositions) });
  }

  function handleSelectedPositions(positions) {
    setSelectedRawPositions(positions);

    // Initialize targets for newly selected positions, clean up deselected
    const newTargets = { ...targets };
    const selectedIds = positions.map((p) => p.id);

    positions.forEach((element) => {
      if (
        newTargets[element.id] == null &&
        !element.isGroup &&
        element.type !== 'percent'
      ) {
        const usePercent = element.type === 'hoaiPhase';
        newTargets[element.id] = round(
          element.amount * (usePercent ? 100 : 1),
          2,
        );
      }
    });

    // Remove targets for deselected positions
    Object.keys(newTargets).forEach((id) => {
      if (!selectedIds.includes(Number(id))) {
        delete newTargets[id];
      }
    });

    setTargets(newTargets);
    processPositions(positions, newTargets);
  }

  function handleTargetCommit(positionId, value, element) {
    const usePercent = element.type === 'hoaiPhase';
    const billedDisplay = (element.billedAmount || 0) * (usePercent ? 100 : 1);
    const totalDisplay = element.amount * (usePercent ? 100 : 1);

    const parsed = parseFloat(value);
    if (isNaN(parsed)) return;

    const clamped = round(
      Math.max(billedDisplay, Math.min(totalDisplay, parsed)),
      4,
    );

    const newTargets = { ...targets, [positionId]: clamped };
    setTargets(newTargets);
    processPositions(selectedRawPositions, newTargets);
  }

  useEffect(() => {
    updateValidation(true);
  }, []);

  function sortByPosition(arr) {
    return arr.sort((a, b) => {
      const posA = a.position.split('.').map(Number);
      const posB = b.position.split('.').map(Number);

      // Compare each part of the position
      for (let i = 0; i < Math.max(posA.length, posB.length); i++) {
        const partA = posA[i] || 0; // Default to 0 if part is undefined
        const partB = posB[i] || 0; // Default to 0 if part is undefined

        if (partA !== partB) {
          return partA - partB;
        }
      }

      // If all parts are equal
      return 0;
    });
  }

  function renderBillingStatus(element, selected) {
    if (element.isGroup || element.type === 'percent') return null;

    const usePercent = element.type === 'hoaiPhase';
    let rawBilled = element.billedAmount || 0;
    if (!rawBilled && element.billedTotal && element.price) {
      rawBilled = round(element.billedTotal / element.price, 4);
    }
    const billedAmount = round(
      rawBilled * (usePercent ? 100 : 1),
      usePercent ? 2 : 4,
    );
    const totalAmount = round(
      element.amount * (usePercent ? 100 : 1),
      usePercent ? 2 : 4,
    );
    const unit = usePercent ? '%' : element.unit || '';
    const decimals = 2;

    if (!selected) {
      return (
        <Tooltip title={t('Billing Status')}>
          <Box
            className="monospace text-right min-w-fit flex flex-col items-end w-32"
            sx={{ ml: 1 }}
          >
            <Typography fontSize={13}>
              {numberToString(billedAmount, decimals)} {unit}
            </Typography>
            <Typography fontSize={11} color="text.secondary">
              / {numberToString(totalAmount, decimals)} {unit}
            </Typography>
          </Box>
        </Tooltip>
      );
    }

    const currentTarget = round(
      targets[element.id] != null ? targets[element.id] : totalAmount,
      decimals,
    );

    return (
      <Box
        className="flex flex-col items-end"
        sx={{ ml: 1, minWidth: 130 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box className="flex items-center gap-1">
          <TargetInput
            value={currentTarget}
            decimals={decimals}
            onCommit={(val) => handleTargetCommit(element.id, val, element)}
          />
          <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
            {unit}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="relative">
      <Box className="flex justify-end px-4 mb-2 mt-1 absolute top-0 right-0">
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ width: 130, textAlign: 'right' }}
        >
          {t('Billing Status')}
        </Typography>
      </Box>
      <SelectPositions
        positions={!isLoading && apiData.data.positions}
        isLoading={isLoading}
        onSelect={handleSelectedPositions}
        allowAll={true}
        requirePercent={true}
        renderExtra={renderBillingStatus}
      />
      {convertedPositions.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t(
            'The following positions were converted to fixed-amount positions due to rounding precision',
          )}
          : {convertedPositions.join(', ')}
        </Alert>
      )}
    </Box>
  );
};

function completePositionsData(positions, orderPositions) {
  if (!positions || !orderPositions) return positions;

  var newPositions = positions.map((pos) => {
    // add the ordered amount and price
    if (pos.isOrderPosition) {
      const orderPosition = orderPositions.find(
        (obj) => obj.id === pos.orderPositionId,
      );
      pos.orderedAmount = orderPosition ? orderPosition.amount : 0;
      pos.orderedPrice = orderPosition ? orderPosition.price : 0;
      pos.totalAmount = orderPosition ? orderPosition.amount : 0;

      // billed
      pos.billedAmount = orderPosition ? orderPosition.billedAmount : 0;
      pos.billedTotal = orderPosition ? orderPosition.billedTotal : 0;
    }

    return pos;
  });

  return newPositions;
}

export default NewProgressBilling;
